import type { FilterJourneyIndex } from '@/src/types/srpJourneyIndex'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useClevertapForSrp from '../../analytics/clevertap/srp'
import useActiveFilterDataIndex from '../useActiveFilterDataIndex'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useSearchResults from '../useSearchResults'

const useTripDurationFilterManager = (filterJourneyIndex: FilterJourneyIndex = 0) => {
    const t = useTranslations('common')
    const { triggerSrpEvent } = useClevertapForSrp()
    const updateCache = useUpdateSearchResultCache()
    const { filterData, isMulticity } = useSearchResults()
    const { filterDataIndex } = useActiveFilterDataIndex(filterJourneyIndex)
    const targetTripDuration = filterData?.tripDuration?.[filterDataIndex]
    const { min: defaultMin, max: defaultMax, current } = targetTripDuration || {}
    const { min: currentMin, max: currentMax } = current || {}

    const title = t('tripDuration')

    const changeTripDurationRange = useCallback(
        (min: number, max: number) => {
            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'trip_duration',
                action_value: `${min}-${max}`
            })

            updateCache(currentData => {
                if (currentData?.filterData?.tripDuration?.[filterDataIndex]) {
                    currentData.filterData.tripDuration[filterDataIndex].current = { min, max }
                }
                return currentData as NonNullable<typeof currentData>
            })
        },
        [filterDataIndex, triggerSrpEvent, updateCache]
    )

    const handleReset = useCallback(() => {
        const numDefaultMin = Number(defaultMin)
        const numDefaultMax = Number(defaultMax)
        if (!isNaN(numDefaultMin) && !isNaN(numDefaultMax)) {
            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'reset',
                action_value: 'trip_duration'
            })

            updateCache(currentData => {
                if (currentData?.filterData?.tripDuration?.[filterDataIndex]) {
                    currentData.filterData.tripDuration[filterDataIndex].current = {
                        min: numDefaultMin,
                        max: numDefaultMax
                    }
                }
                return currentData as NonNullable<typeof currentData>
            })
        }
    }, [defaultMax, defaultMin, filterDataIndex, triggerSrpEvent, updateCache])

    const isTripDurationFilterApplied = useMemo(() => {
        return currentMin !== defaultMin || currentMax !== defaultMax
    }, [currentMax, currentMin, defaultMax, defaultMin])

    const shouldShowResetForAllJourneys = useMemo(() => {
        if (!isMulticity) return false
        return !!(filterData?.tripDuration?.some(td => td.current?.min !== td.min || td.current?.max !== td.max))
    }, [isMulticity, filterData])

    const handleResetAllJourneys = useCallback(() => {
        triggerSrpEvent('Air_UI_Action', {
            action_name: 'filter',
            action_type: 'reset',
            action_value: 'trip_duration'
        })

        updateCache(currentData => {
            currentData?.filterData?.tripDuration?.forEach(td => {
                td.current = { min: td.min, max: td.max }
            })
            return currentData as NonNullable<typeof currentData>
        })
    }, [triggerSrpEvent, updateCache])

    return {
        title,
        min: defaultMin,
        max: defaultMax,
        currentMin,
        currentMax,
        changeTripDurationRange,
        handleReset,
        handleResetAllJourneys,
        isTripDurationFilterApplied,
        shouldShowResetForAllJourneys
    }
}

export default useTripDurationFilterManager
